import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Users, Globe, Bot, Clock, Send, MessageSquare } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axios from 'axios';

function WebsiteAnalytics() {
  const [summary, setSummary] = useState(null);
  const [dailyStats, setDailyStats] = useState([]);
  const [hourlyStats, setHourlyStats] = useState([]);
  const [topPages, setTopPages] = useState([]);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState(30);
  const [viewMode, setViewMode] = useState('daily');
  const [loadingAI, setLoadingAI] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    loadAnalytics();
  }, [dateRange, viewMode]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      if (viewMode === 'hourly' && dateRange === 1) {
        const [summaryRes, hourlyRes, pagesRes] = await Promise.all([
          axios.get(`/api/analytics/website/summary?days=${dateRange}`, { headers }),
          axios.get(`/api/analytics/website/hourly?hours=24`, { headers }),
          axios.get(`/api/analytics/website/pages?days=${dateRange}&limit=10`, { headers })
        ]);
        setSummary(summaryRes.data);
        setHourlyStats(hourlyRes.data.hourly_stats);
        setDailyStats([]);
        setTopPages(pagesRes.data.top_pages);
      } else {
        const [summaryRes, dailyRes, pagesRes] = await Promise.all([
          axios.get(`/api/analytics/website/summary?days=${dateRange}`, { headers }),
          axios.get(`/api/analytics/website/daily?days=${dateRange}`, { headers }),
          axios.get(`/api/analytics/website/pages?days=${dateRange}&limit=10`, { headers })
        ]);
        setSummary(summaryRes.data);
        setDailyStats(dailyRes.data.daily_stats);
        setHourlyStats([]);
        setTopPages(pagesRes.data.top_pages);
      }
    } catch (err) {
      setError('Failed to load analytics: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const loadAIAnalysis = async () => {
    try {
      setLoadingAI(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/analytics/website/ai-analysis?days=${dateRange}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAiAnalysis(response.data);
      setChatMessages([{
        role: 'assistant',
        content: response.data.ai_analysis,
        timestamp: new Date().toISOString()
      }]);
    } catch (err) {
      setError('Failed to load AI analysis: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoadingAI(false);
    }
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim() || sendingMessage) return;
    const userMessage = chatInput.trim();
    setChatInput('');
    setSendingMessage(true);
    const newMessages = [...chatMessages, {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    }];
    setChatMessages(newMessages);
    
    try {
      const token = localStorage.getItem('token');
      const analyticsContext = aiAnalysis?.analytics_context || '';
      const conversationHistory = newMessages.map(m => ({
        role: m.role,
        content: m.content
      }));
      
      const response = await axios.post(
        `/api/analytics/website/ai-chat`,
        {
          message: userMessage,
          analytics_context: analyticsContext,
          conversation_history: conversationHistory,
          days: dateRange
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setChatMessages([...newMessages, {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString()
      }]);
    } catch (err) {
      setChatMessages([...newMessages, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChatMessage();
    }
  };

  const formatDateTime = (datetime) => {
    const date = new Date(datetime);
    if (viewMode === 'hourly') {
      return date.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  const chartData = viewMode === 'hourly' ? hourlyStats : dailyStats;
  const xAxisKey = viewMode === 'hourly' ? 'datetime' : 'date';

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <h2 className="text-2xl font-bold text-gray-800">Website Analytics</h2>
        <div className="flex gap-3">
          <select
            value={dateRange}
            onChange={(e) => {
              const value = Number(e.target.value);
              setDateRange(value);
              if (value === 1) {
                setViewMode('hourly');
              } else {
                setViewMode('daily');
              }
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value={1}>Last 24 Hours</option>
            <option value={7}>Last 7 Days</option>
            <option value={14}>Last 14 Days</option>
            <option value={30}>Last 30 Days</option>
            <option value={90}>Last 90 Days</option>
            <option value={180}>Last 6 Months</option>
            <option value={365}>Last Year</option>
            <option value={-1}>All Time</option>
          </select>
          {dateRange === 1 && (
            <button
              onClick={() => setViewMode(viewMode === 'hourly' ? 'daily' : 'hourly')}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2"
            >
              <Clock className="w-4 h-4" />
              {viewMode === 'hourly' ? 'Daily View' : 'Hourly View'}
            </button>
          )}
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Hits</p>
                <p className="text-2xl font-bold text-gray-800">{summary.total_hits.toLocaleString()}</p>
              </div>
              <BarChart3 className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Unique Visitors</p>
                <p className="text-2xl font-bold text-gray-800">{summary.unique_visitors.toLocaleString()}</p>
              </div>
              <Users className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Human Traffic</p>
                <p className="text-2xl font-bold text-gray-800">{summary.human_traffic.toLocaleString()}</p>
              </div>
              <Globe className="w-8 h-8 text-cyan-500" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Bot Traffic</p>
                <p className="text-2xl font-bold text-gray-800">{summary.bot_traffic.toLocaleString()}</p>
              </div>
              <Bot className="w-8 h-8 text-orange-500" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Bandwidth</p>
                <p className="text-2xl font-bold text-gray-800">{summary.total_bandwidth_mb.toFixed(1)} MB</p>
              </div>
              <TrendingUp className="w-8 h-8 text-indigo-500" />
            </div>
          </div>
        </div>
      )}

      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          {viewMode === 'hourly' ? 'Hourly Traffic (Last 24 Hours) - Eastern Time' : 'Daily Traffic'}
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey={xAxisKey} 
              tickFormatter={formatDateTime}
              angle={viewMode === 'hourly' ? -45 : 0}
              textAnchor={viewMode === 'hourly' ? 'end' : 'middle'}
              height={viewMode === 'hourly' ? 80 : 30}
            />
            <YAxis />
            <Tooltip labelFormatter={formatDateTime} />
            <Legend />
            <Line type="monotone" dataKey="human_hits" stroke="#8b5cf6" name="Human Visitors" strokeWidth={2} />
            <Line type="monotone" dataKey="bot_hits" stroke="#f97316" name="Bots" strokeWidth={2} />
            <Line type="monotone" dataKey="unique_visitors" stroke="#10b981" name="Unique IPs" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Top Pages</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Page</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hits</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unique Visitors</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {topPages.map((page, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 text-sm text-gray-900 font-mono">{page.path}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{page.hits.toLocaleString()}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{page.unique_visitors.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-800">Astraea AI Analytics Assistant</h3>
          </div>
          {!aiAnalysis && (
            <button
              onClick={loadAIAnalysis}
              disabled={loadingAI}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center space-x-2"
            >
              {loadingAI ? <span>Analyzing...</span> : <><TrendingUp className="w-4 h-4" /><span>Start Analysis</span></>}
            </button>
          )}
        </div>
        
        {chatMessages.length > 0 && (
          <div className="bg-gray-50 rounded-lg p-4 mb-4 max-h-96 overflow-y-auto">
            {chatMessages.map((msg, idx) => (
              <div key={idx} className={`mb-4 ${msg.role === 'user' ? 'text-right' : 'text-left'}`}>
                <div className={`inline-block max-w-[80%] p-3 rounded-lg ${
                  msg.role === 'user' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-800'
                }`}>
                  <div className="text-xs opacity-70 mb-1">{msg.role === 'user' ? 'You' : 'Astraea AI'}</div>
                  <div className="whitespace-pre-wrap text-sm">{msg.content}</div>
                </div>
              </div>
            ))}
            {sendingMessage && (
              <div className="text-left">
                <div className="inline-block bg-white border border-gray-200 p-3 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-500">
                    <div className="animate-pulse">Astraea is thinking...</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {aiAnalysis && (
          <div className="flex gap-2">
            <textarea
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask Astraea about your analytics..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
              rows={2}
              disabled={sendingMessage}
            />
            <button
              onClick={sendChatMessage}
              disabled={sendingMessage || !chatInput.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center justify-center"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        )}
        
        {!aiAnalysis && !loadingAI && (
          <div className="text-center py-8 text-gray-500">
            Click "Start Analysis" to begin a conversation with Astraea about your website analytics.
          </div>
        )}
      </div>
    </div>
  );
}

export default WebsiteAnalytics;
